(() => {
  'use strict';

  const STORAGE_KEY = 'revibras_radar_location';
  const LEGACY_STORAGE_KEY = 'revibras_artmeter_location';
  const GEOCODE_CACHE_KEY = 'revibras_radar_geocode_cache_v1';
  const STORAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const IP_LOCATION_ENDPOINT = 'https://ipwho.is/';
  const NOMINATIM_SEARCH_ENDPOINT = 'https://nominatim.openstreetmap.org/search';
  const NOMINATIM_REVERSE_ENDPOINT = 'https://nominatim.openstreetmap.org/reverse';
  const MAPLIBRE_VERSION = '5.6.1';

  const mapStyles = {
    dark: 'https://tiles.openfreemap.org/styles/dark',
    positron: 'https://tiles.openfreemap.org/styles/positron',
    liberty: 'https://tiles.openfreemap.org/styles/liberty'
  };

  const sections = document.querySelectorAll('[data-radar], [data-artmeter]');
  if (!sections.length) return;

  const normalizeKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[áàäâãå]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöôõ]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ');

  const canonicalCity = (city, country) => {
    let normalized = normalizeKey(city);
    const normalizedCountry = normalizeKey(country);

    if (normalizedCountry && normalized.endsWith(` de ${normalizedCountry}`)) {
      normalized = normalized.slice(0, -(normalizedCountry.length + 4)).trim();
    }

    return normalized
      .replace(/^stgo\.?$/, 'santiago')
      .replace(/^santiago de chile$/, 'santiago')
      .replace(/^cdmx$/, 'ciudad de mexico')
      .replace(/^bs\.?\s?as\.?$/, 'buenos aires');
  };

  const parseJSON = (element, fallback) => {
    const raw = String(element?.textContent || '').trim();
    if (!raw) return fallback;

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('RADAR: JSON inválido.', error);
      return fallback;
    }
  };

  const formatRelativeTime = (isoDate) => {
    if (!isoDate) return 'Recientemente';

    const date = new Date(isoDate);
    const difference = Date.now() - date.getTime();

    if (!Number.isFinite(difference)) return 'Recientemente';

    const minutes = Math.max(0, Math.floor(difference / 60000));
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes} min`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? 'Hace 1 hora' : `Hace ${hours} horas`;

    const days = Math.floor(hours / 24);
    if (days < 7) return days === 1 ? 'Hace 1 día' : `Hace ${days} días`;

    return new Intl.DateTimeFormat('es-CL', {
      day: 'numeric',
      month: 'short'
    }).format(date);
  };

  const countryFlag = (countryCode) => {
    const code = String(countryCode || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return '◎';

    return String.fromCodePoint(
      ...code.split('').map((character) => 127397 + character.charCodeAt())
    );
  };

  const sleep = (milliseconds) => new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

  let mapLibrePromise = null;

  const ensureStylesheet = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.radarMaplibre = 'true';
    document.head.appendChild(link);
  };

  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      if (window.maplibregl) {
        resolve(window.maplibregl);
        return;
      }

      existing.addEventListener('load', () => resolve(window.maplibregl), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  const loadMapLibre = () => {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (mapLibrePromise) return mapLibrePromise;

    const cssUrl = `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`;
    ensureStylesheet(cssUrl);

    const scripts = [
      `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`,
      `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`
    ];

    mapLibrePromise = scripts.reduce(
      (promise, src) => promise.catch(() => loadScript(src)),
      Promise.reject(new Error('Inicializando MapLibre'))
    ).then((library) => {
      if (!library) throw new Error('MapLibre no está disponible.');
      return library;
    });

    return mapLibrePromise;
  };

  const readCache = () => {
    try {
      return JSON.parse(localStorage.getItem(GEOCODE_CACHE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  };

  const writeCache = (cache) => {
    try {
      localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      // Local storage is optional.
    }
  };

  let lastGeocodeRequestAt = 0;

  const throttleGeocoder = async () => {
    const elapsed = Date.now() - lastGeocodeRequestAt;
    if (elapsed < 1100) await sleep(1100 - elapsed);
    lastGeocodeRequestAt = Date.now();
  };

  const geocodeLocation = async (city, country) => {
    const cacheKey = `search:${normalizeKey(country)}:${canonicalCity(city, country)}`;
    const cache = readCache();

    if (cache[cacheKey]) return cache[cacheKey];

    await throttleGeocoder();

    const url = new URL(NOMINATIM_SEARCH_ENDPOINT);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('limit', '1');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('accept-language', 'es');
    url.searchParams.set('city', city);
    url.searchParams.set('country', country);

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      referrerPolicy: 'strict-origin-when-cross-origin'
    });

    if (!response.ok) throw new Error('No pudimos ubicar esa ciudad.');

    const results = await response.json();
    const first = results?.[0];
    if (!first) throw new Error('No encontramos esa ciudad. Revisa el nombre.');

    const result = {
      city: first.address?.city || first.address?.town || first.address?.village || first.address?.municipality || city,
      country: first.address?.country || country,
      countryCode: String(first.address?.country_code || '').toUpperCase(),
      latitude: String(first.lat || ''),
      longitude: String(first.lon || '')
    };

    cache[cacheKey] = result;
    writeCache(cache);
    return result;
  };

  const reverseGeocode = async (latitude, longitude) => {
    const roundedLatitude = Number(latitude).toFixed(3);
    const roundedLongitude = Number(longitude).toFixed(3);
    const cacheKey = `reverse:${roundedLatitude}:${roundedLongitude}`;
    const cache = readCache();

    if (cache[cacheKey]) return cache[cacheKey];

    await throttleGeocoder();

    const url = new URL(NOMINATIM_REVERSE_ENDPOINT);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('accept-language', 'es');
    url.searchParams.set('lat', latitude);
    url.searchParams.set('lon', longitude);
    url.searchParams.set('zoom', '10');

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      referrerPolicy: 'strict-origin-when-cross-origin'
    });

    if (!response.ok) throw new Error('No pudimos interpretar la ubicación.');

    const resultJson = await response.json();
    const address = resultJson.address || {};

    const result = {
      city: address.city || address.town || address.village || address.municipality || address.county || '',
      country: address.country || '',
      countryCode: String(address.country_code || '').toUpperCase(),
      latitude: String(latitude),
      longitude: String(longitude)
    };

    cache[cacheKey] = result;
    writeCache(cache);
    return result;
  };

  sections.forEach((section) => {
    if (section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const fallback = {
      version: 1,
      updatedAt: null,
      summary: { registrations: 0, countries: 0, cities: 0, heatLevel: 1 },
      countries: [],
      cities: [],
      activity: []
    };

    const rawData = parseJSON(section.querySelector('[data-artmeter-data]'), fallback);
    rawData.summary = rawData.summary || fallback.summary;
    rawData.countries = Array.isArray(rawData.countries) ? rawData.countries : [];
    rawData.cities = Array.isArray(rawData.cities) ? rawData.cities : [];
    rawData.activity = Array.isArray(rawData.activity) ? rawData.activity : [];

    const heatNames = {
      1: section.dataset.heatName1 || 'Señal inicial',
      2: section.dataset.heatName2 || 'Calentando',
      3: section.dataset.heatName3 || 'Impulso',
      4: section.dataset.heatName4 || 'Alta demanda',
      5: section.dataset.heatName5 || 'Lista para evaluar'
    };

    const heatDescriptions = {
      1: 'La señal está comenzando. Cada registro tiene un impacto visible.',
      2: 'La comunidad empieza a movilizarse alrededor de este artista.',
      3: 'La comunidad está construyendo una señal consistente.',
      4: 'La demanda está mostrando una tracción importante.',
      5: 'Esta ciudad ya está enviando una señal difícil de ignorar.'
    };

    const heatFromCount = (count, type) => {
      const value = Number(count) || 0;

      if (type === 'country') {
        if (value >= 750) return 5;
        if (value >= 350) return 4;
        if (value >= 150) return 3;
        if (value >= 50) return 2;
        return 1;
      }

      if (value >= 300) return 5;
      if (value >= 150) return 4;
      if (value >= 75) return 3;
      if (value >= 25) return 2;
      return 1;
    };

    const countryAliases = new Map();

    [...rawData.countries, ...rawData.cities].forEach((entry) => {
      const name = normalizeKey(entry.name || entry.country);
      const code = String(entry.code || entry.countryCode || '').trim().toUpperCase();
      if (name && code) countryAliases.set(code, name);
    });

    const canonicalCountry = (name, code) => {
      const normalizedName = normalizeKey(name);
      const normalizedCode = String(code || '').trim().toUpperCase();
      if (normalizedName) return normalizedName;
      if (normalizedCode && countryAliases.has(normalizedCode)) return countryAliases.get(normalizedCode);
      return normalizeKey(normalizedCode);
    };

    const countriesMap = new Map();

    rawData.countries.forEach((country) => {
      const key = canonicalCountry(country.name, country.code);
      if (!key) return;

      const current = countriesMap.get(key) || {
        key,
        code: String(country.code || '').trim().toUpperCase(),
        name: String(country.name || '').trim(),
        registrations: 0
      };

      current.registrations += Number(country.registrations) || 0;
      if (!current.code && country.code) current.code = String(country.code).trim().toUpperCase();
      if (!current.name && country.name) current.name = String(country.name).trim();
      countriesMap.set(key, current);
    });

    const citiesMap = new Map();

    rawData.cities.forEach((city) => {
      const countryKey = canonicalCountry(city.country, city.countryCode);
      const cityKey = canonicalCity(city.name, city.country);
      if (!countryKey || !cityKey) return;

      const key = `${countryKey}|${cityKey}`;
      const current = citiesMap.get(key) || {
        key,
        name: String(city.name || '').trim(),
        country: String(city.country || '').trim(),
        countryCode: String(city.countryCode || '').trim().toUpperCase(),
        latitude: null,
        longitude: null,
        registrations: 0
      };

      current.registrations += Number(city.registrations) || 0;

      const latitude = Number(city.latitude);
      const longitude = Number(city.longitude);
      if (current.latitude === null && Number.isFinite(latitude)) current.latitude = latitude;
      if (current.longitude === null && Number.isFinite(longitude)) current.longitude = longitude;
      if (!current.countryCode && city.countryCode) current.countryCode = String(city.countryCode).trim().toUpperCase();
      if (!current.country && city.country) current.country = String(city.country).trim();

      if (normalizeKey(current.name).includes(' de ') && !normalizeKey(city.name).includes(' de ')) {
        current.name = String(city.name).trim();
      }

      citiesMap.set(key, current);
    });

    const data = {
      ...rawData,
      countries: Array.from(countriesMap.values())
        .map((country) => {
          const heatLevel = Number(country.heatLevel) || heatFromCount(country.registrations, 'country');
          return { ...country, heatLevel, heatLabel: heatNames[heatLevel] };
        })
        .sort((a, b) => b.registrations - a.registrations),
      cities: Array.from(citiesMap.values())
        .map((city) => {
          const heatLevel = Number(city.heatLevel) || heatFromCount(city.registrations, 'city');
          return { ...city, heatLevel, heatLabel: heatNames[heatLevel] };
        })
        .sort((a, b) => b.registrations - a.registrations)
    };

    const form = section.querySelector('[data-artmeter-form]');
    const submit = section.querySelector('[data-artmeter-submit]');
    const submitLabel = section.querySelector('[data-artmeter-submit-label]');
    const message = section.querySelector('[data-artmeter-form-message]');
    const editor = section.querySelector('[data-artmeter-location-editor]');
    const countryInput = section.querySelector('[data-artmeter-country-input]');
    const cityInput = section.querySelector('[data-artmeter-city-input]');
    const saveLocationButton = section.querySelector('[data-artmeter-save-location]');
    const locationLabel = section.querySelector('[data-artmeter-location-label]');
    const floatingLocation = section.querySelector('[data-artmeter-floating-location]');
    const countryProperty = section.querySelector('[data-artmeter-property-country]');
    const cityProperty = section.querySelector('[data-artmeter-property-city]');
    const codeProperty = section.querySelector('[data-artmeter-property-country-code]');
    const latitudeProperty = section.querySelector('[data-artmeter-property-latitude]');
    const longitudeProperty = section.querySelector('[data-artmeter-property-longitude]');
    const selectedHeat = section.querySelector('[data-artmeter-selected-heat]');
    const selectedHeatLabel = section.querySelector('[data-artmeter-heat-label]');
    const selectedHeatCopy = section.querySelector('[data-artmeter-heat-copy]');
    const tickerCopy = section.querySelector('[data-artmeter-ticker-copy]');
    const mapCanvas = section.querySelector('[data-radar-map-canvas]');
    const mapLoading = section.querySelector('[data-radar-map-loading]');
    const mapError = section.querySelector('[data-radar-map-error]');
    const mapHome = section.querySelector('[data-radar-map-home]');
    const mapRetry = section.querySelector('[data-radar-map-retry]');

    const state = {
      country: '',
      countryCode: '',
      city: '',
      latitude: '',
      longitude: '',
      source: '',
      savedAt: 0
    };

    let map = null;
    let homeBounds = null;
    let userMarker = null;
    let tickerTimer = null;

    const setMessage = (text, isError = false) => {
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('is-error', isError);
      message.hidden = !text;
    };

    const findSelectedCity = () => data.cities.find((city) => (
      canonicalCountry(city.country, city.countryCode) === canonicalCountry(state.country, state.countryCode) &&
      canonicalCity(city.name, city.country) === canonicalCity(state.city, state.country)
    ));

    const setHeatClass = (element, baseClass, level) => {
      if (!element) return;
      for (let index = 1; index <= 5; index += 1) element.classList.remove(`${baseClass}--${index}`);
      element.classList.add(`${baseClass}--${level}`);
    };

    const renderSelectedHeat = () => {
      const selectedCity = findSelectedCity();
      const level = Math.min(5, Math.max(1, Number(selectedCity?.heatLevel) || 1));

      setHeatClass(selectedHeat, 'artmeter__selected-heat', level);
      if (selectedHeatLabel) selectedHeatLabel.textContent = selectedCity ? heatNames[level] : 'Señal inicial';
      if (selectedHeatCopy) selectedHeatCopy.textContent = selectedCity ? heatDescriptions[level] : 'Tu registro puede activar esta ciudad.';

      selectedHeat?.querySelectorAll('.artmeter__heat-scale span').forEach((segment, index) => {
        segment.classList.toggle('is-active', index < level);
      });
    };

    const updateUserMarker = () => {
      if (!map || !window.maplibregl) return;

      const latitude = Number(state.latitude);
      const longitude = Number(state.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

      if (!userMarker) {
        const element = document.createElement('div');
        element.className = 'artmeter__user-marker';
        element.setAttribute('aria-label', 'Tu ubicación aproximada');

        userMarker = new window.maplibregl.Marker({ element, anchor: 'center' })
          .setLngLat([longitude, latitude])
          .addTo(map);
      } else {
        userMarker.setLngLat([longitude, latitude]);
      }
    };

    const updateLocationUI = () => {
      const valid = Boolean(state.country && state.city);
      const text = valid ? `${state.city}, ${state.country}` : 'Ubicación por confirmar';

      if (locationLabel) locationLabel.textContent = text;
      if (floatingLocation) floatingLocation.textContent = valid ? state.city : 'Registrar mi ciudad';
      if (countryProperty) countryProperty.value = state.country;
      if (cityProperty) cityProperty.value = state.city;
      if (codeProperty) codeProperty.value = state.countryCode;
      if (latitudeProperty) latitudeProperty.value = state.latitude;
      if (longitudeProperty) longitudeProperty.value = state.longitude;
      if (countryInput && !countryInput.matches(':focus')) countryInput.value = state.country;
      if (cityInput && !cityInput.matches(':focus')) cityInput.value = state.city;
      if (submit) submit.disabled = !valid;

      renderSelectedHeat();
      updateUserMarker();
    };

    const saveLocation = () => {
      state.savedAt = Date.now();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch (error) {
        // Optional storage.
      }
    };

    const loadSavedLocation = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
        const saved = JSON.parse(raw || 'null');
        if (!saved?.country || !saved?.city) return false;

        const isManual = saved.source === 'manual';
        const isFresh = Number(saved.savedAt) > 0 && Date.now() - Number(saved.savedAt) < STORAGE_MAX_AGE_MS;
        if (!isManual && !isFresh) return false;

        Object.assign(state, saved);
        updateLocationUI();
        saveLocation();
        return true;
      } catch (error) {
        return false;
      }
    };

    const openLocationEditor = () => {
      if (!editor) return;
      editor.hidden = false;
      countryInput?.focus();
    };

    const closeLocationEditor = () => {
      if (editor) editor.hidden = true;
    };

    const detectLocationByIP = async () => {
      try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 7000);
        const response = await fetch(IP_LOCATION_ENDPOINT, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
          cache: 'no-store'
        });
        window.clearTimeout(timeout);

        if (!response.ok) throw new Error('No se pudo detectar la ubicación.');
        const result = await response.json();
        if (result.success === false) throw new Error(result.message || 'Ubicación no disponible.');

        Object.assign(state, {
          country: String(result.country || '').trim(),
          countryCode: String(result.country_code || '').trim().toUpperCase(),
          city: String(result.city || '').trim(),
          latitude: Number.isFinite(Number(result.latitude)) ? String(result.latitude) : '',
          longitude: Number.isFinite(Number(result.longitude)) ? String(result.longitude) : '',
          source: 'ip',
          savedAt: Date.now()
        });

        if (!state.country || !state.city) throw new Error('Ubicación incompleta.');

        if (!state.latitude || !state.longitude) {
          const geocoded = await geocodeLocation(state.city, state.country);
          Object.assign(state, geocoded, { source: 'ip+geocoding', savedAt: Date.now() });
        }

        updateLocationUI();
        saveLocation();
        closeLocationEditor();
      } catch (error) {
        openLocationEditor();
        setMessage('No pudimos detectar tu ciudad automáticamente. Confírmala para continuar.');
      }
    };

    const improveWithGrantedGeolocation = async () => {
      if (!navigator.permissions || !navigator.geolocation) return;

      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') return;

        navigator.geolocation.getCurrentPosition(async (position) => {
          try {
            const precise = await reverseGeocode(position.coords.latitude, position.coords.longitude);
            if (!precise.city || !precise.country) return;

            Object.assign(state, precise, { source: 'precise', savedAt: Date.now() });
            updateLocationUI();
            saveLocation();
          } catch (error) {
            // IP location remains valid.
          }
        }, () => {}, {
          enableHighAccuracy: false,
          maximumAge: 30 * 60 * 1000,
          timeout: 5000
        });
      } catch (error) {
        // Permissions API is optional.
      }
    };

    const confirmManualLocation = async () => {
      const country = countryInput?.value.trim() || '';
      const city = cityInput?.value.trim() || '';

      if (!country || !city) {
        setMessage('Escribe un país y una ciudad para continuar.', true);
        return;
      }

      const knownCity = data.cities.find((item) => (
        canonicalCountry(item.country, item.countryCode) === canonicalCountry(country, '') &&
        canonicalCity(item.name, item.country) === canonicalCity(city, country)
      ));

      const originalText = saveLocationButton?.textContent || 'Confirmar ubicación';
      if (saveLocationButton) {
        saveLocationButton.disabled = true;
        saveLocationButton.textContent = 'Ubicando…';
      }

      try {
        const resolved = knownCity
          ? {
              city: knownCity.name,
              country: knownCity.country,
              countryCode: knownCity.countryCode,
              latitude: String(knownCity.latitude ?? ''),
              longitude: String(knownCity.longitude ?? '')
            }
          : await geocodeLocation(city, country);

        Object.assign(state, resolved, {
          source: 'manual',
          savedAt: Date.now()
        });

        updateLocationUI();
        saveLocation();
        closeLocationEditor();
        setMessage('');
      } catch (error) {
        setMessage(error.message || 'No pudimos ubicar esa ciudad.', true);
      } finally {
        if (saveLocationButton) {
          saveLocationButton.disabled = false;
          saveLocationButton.textContent = originalText;
        }
      }
    };

    const mapFeatures = () => data.cities
      .filter((city) => {
        const latitude = Number(city.latitude);
        const longitude = Number(city.longitude);
        return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
      })
      .map((city) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(city.longitude), Number(city.latitude)]
        },
        properties: {
          name: city.name,
          country: city.country,
          countryCode: city.countryCode,
          heatLevel: Number(city.heatLevel) || 1,
          heatLabel: heatNames[Number(city.heatLevel) || 1]
        }
      }));

    const cssColor = (name, fallbackColor) => (
      getComputedStyle(section).getPropertyValue(name).trim() || fallbackColor
    );

    const createPopupContent = (properties) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'artmeter__popup';

      const kicker = document.createElement('span');
      kicker.className = 'artmeter__popup-kicker';
      kicker.textContent = 'Señal de apoyo';

      const title = document.createElement('strong');
      title.textContent = `${countryFlag(properties.countryCode)} ${properties.name}, ${properties.country}`;

      const status = document.createElement('span');
      status.className = 'artmeter__popup-status';
      status.textContent = properties.heatLabel || heatNames[1];

      wrapper.append(kicker, title, status);
      return wrapper;
    };

    const fitMapHome = (features, animate = false) => {
      if (!map || !window.maplibregl) return;

      if (!features.length) {
        map.easeTo({ center: [-20, 12], zoom: 1.35, duration: animate ? 650 : 0 });
        return;
      }

      const bounds = new window.maplibregl.LngLatBounds();
      features.forEach((feature) => bounds.extend(feature.geometry.coordinates));
      homeBounds = bounds;

      if (features.length === 1) {
        map.easeTo({ center: features[0].geometry.coordinates, zoom: 3.6, duration: animate ? 650 : 0 });
      } else {
        map.fitBounds(bounds, {
          padding: window.innerWidth < 600 ? 42 : 80,
          maxZoom: 4.5,
          duration: animate ? 650 : 0
        });
      }
    };

    const initializeMap = async () => {
      if (!mapCanvas || map) return;

      if (mapLoading) mapLoading.hidden = false;
      if (mapError) mapError.hidden = true;

      try {
        const maplibregl = await loadMapLibre();
        const features = mapFeatures();

        map = new maplibregl.Map({
          container: mapCanvas,
          style: mapStyles[section.dataset.mapStyle] || mapStyles.dark,
          center: [-20, 12],
          zoom: 1.35,
          minZoom: 1,
          maxZoom: 12,
          dragPan: true,
          touchZoomRotate: true,
          doubleClickZoom: true,
          cooperativeGestures: false,
          attributionControl: true
        });

        map.touchZoomRotate.disableRotation();
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

        let loaded = false;
        const loadTimeout = window.setTimeout(() => {
          if (!loaded) {
            map?.remove();
            map = null;
            if (mapLoading) mapLoading.hidden = true;
            if (mapError) mapError.hidden = false;
          }
        }, 14000);

        map.on('load', () => {
          loaded = true;
          window.clearTimeout(loadTimeout);
          if (mapLoading) mapLoading.hidden = true;

          map.addSource('radar-demand', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features },
            cluster: true,
            clusterMaxZoom: 7,
            clusterRadius: 48,
            clusterProperties: {
              maxHeat: ['max', ['get', 'heatLevel']]
            }
          });

          map.addLayer({
            id: 'radar-cluster-glow',
            type: 'circle',
            source: 'radar-demand',
            filter: ['has', 'point_count'],
            paint: {
              'circle-radius': ['step', ['get', 'point_count'], 30, 5, 39, 15, 49],
              'circle-color': [
                'match', ['get', 'maxHeat'],
                1, cssColor('--am-heat-1', '#777b80'),
                2, cssColor('--am-heat-2', '#a86458'),
                3, cssColor('--am-heat-3', '#c34d3f'),
                4, cssColor('--am-heat-4', '#e23828'),
                cssColor('--am-heat-5', '#ff271c')
              ],
              'circle-opacity': 0.18,
              'circle-blur': 0.55
            }
          });

          map.addLayer({
            id: 'radar-clusters',
            type: 'circle',
            source: 'radar-demand',
            filter: ['has', 'point_count'],
            paint: {
              'circle-radius': ['step', ['get', 'point_count'], 16, 5, 21, 15, 27],
              'circle-color': [
                'match', ['get', 'maxHeat'],
                1, cssColor('--am-heat-1', '#777b80'),
                2, cssColor('--am-heat-2', '#a86458'),
                3, cssColor('--am-heat-3', '#c34d3f'),
                4, cssColor('--am-heat-4', '#e23828'),
                cssColor('--am-heat-5', '#ff271c')
              ],
              'circle-stroke-color': cssColor('--am-text', '#ffffff'),
              'circle-stroke-width': 1.2
            }
          });

          map.addLayer({
            id: 'radar-cluster-count',
            type: 'symbol',
            source: 'radar-demand',
            filter: ['has', 'point_count'],
            layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-size': 10 },
            paint: { 'text-color': cssColor('--am-text', '#ffffff') }
          });

          map.addLayer({
            id: 'radar-point-glow',
            type: 'circle',
            source: 'radar-demand',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 18, 5, 28, 9, 38],
              'circle-color': [
                'match', ['get', 'heatLevel'],
                1, cssColor('--am-heat-1', '#777b80'),
                2, cssColor('--am-heat-2', '#a86458'),
                3, cssColor('--am-heat-3', '#c34d3f'),
                4, cssColor('--am-heat-4', '#e23828'),
                cssColor('--am-heat-5', '#ff271c')
              ],
              'circle-opacity': 0.2,
              'circle-blur': 0.58
            }
          });

          map.addLayer({
            id: 'radar-points',
            type: 'circle',
            source: 'radar-demand',
            filter: ['!', ['has', 'point_count']],
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 7, 5, 9, 9, 12],
              'circle-color': [
                'match', ['get', 'heatLevel'],
                1, cssColor('--am-heat-1', '#777b80'),
                2, cssColor('--am-heat-2', '#a86458'),
                3, cssColor('--am-heat-3', '#c34d3f'),
                4, cssColor('--am-heat-4', '#e23828'),
                cssColor('--am-heat-5', '#ff271c')
              ],
              'circle-stroke-color': cssColor('--am-text', '#ffffff'),
              'circle-stroke-width': 1.25
            }
          });

          map.on('click', 'radar-clusters', async (event) => {
            const feature = event.features?.[0];
            if (!feature) return;

            try {
              const source = map.getSource('radar-demand');
              const zoom = await source.getClusterExpansionZoom(feature.properties.cluster_id);
              map.easeTo({ center: feature.geometry.coordinates, zoom, duration: 650 });
            } catch (error) {
              // Keep map usable if cluster expansion fails.
            }
          });

          map.on('click', 'radar-points', (event) => {
            const feature = event.features?.[0];
            if (!feature) return;

            new maplibregl.Popup({ closeButton: true, offset: 14, maxWidth: '280px' })
              .setLngLat(feature.geometry.coordinates)
              .setDOMContent(createPopupContent(feature.properties || {}))
              .addTo(map);
          });

          ['radar-clusters', 'radar-points'].forEach((layerId) => {
            map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
            map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
          });

          fitMapHome(features, false);
          updateUserMarker();
          window.setTimeout(() => map.resize(), 80);
        });
      } catch (error) {
        console.warn('RADAR: no se pudo cargar el mapa.', error);
        if (mapLoading) mapLoading.hidden = true;
        if (mapError) mapError.hidden = false;
      }
    };

    const activityMessage = (entry) => {
      const city = data.cities.find((item) => (
        canonicalCity(item.name, item.country) === canonicalCity(entry.city, entry.country) &&
        canonicalCountry(item.country, item.countryCode) === canonicalCountry(entry.country, entry.countryCode)
      ));

      return (Number(city?.registrations) || 0) <= 1
        ? `${entry.city} se sumó al RADAR`
        : `${entry.city} registró una nueva señal`;
    };

    const startTicker = () => {
      if (!tickerCopy) return;

      const messages = data.activity.length
        ? data.activity.slice(0, 6).map((entry) => `${countryFlag(entry.countryCode)} ${activityMessage(entry)} · ${formatRelativeTime(entry.createdAt)}`)
        : ['Sé la primera persona en activar tu ciudad.'];

      tickerCopy.textContent = messages[0];
      if (messages.length < 2) return;

      let index = 0;
      tickerTimer = window.setInterval(() => {
        index = (index + 1) % messages.length;
        tickerCopy.classList.add('is-changing');
        window.setTimeout(() => {
          tickerCopy.textContent = messages[index];
          tickerCopy.classList.remove('is-changing');
        }, 220);
      }, 4300);
    };

    const initializeReveal = () => {
      const items = section.querySelectorAll('.artmeter__reveal');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.06, rootMargin: '0px 0px -4% 0px' });

      items.forEach((item) => observer.observe(item));
    };

    section.querySelectorAll('[data-relative-time]').forEach((element) => {
      element.textContent = formatRelativeTime(element.dataset.relativeTime);
    });

    section.querySelectorAll('[data-updated-at]').forEach((element) => {
      if (element.dataset.updatedAt) element.textContent = formatRelativeTime(element.dataset.updatedAt);
    });

    section.querySelector('[data-artmeter-change-location]')?.addEventListener('click', openLocationEditor);
    saveLocationButton?.addEventListener('click', confirmManualLocation);

    [countryInput, cityInput].forEach((input) => {
      input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        confirmManualLocation();
      });
    });

    section.querySelectorAll('[data-artmeter-scroll-to-form]').forEach((button) => {
      button.addEventListener('click', () => {
        form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    mapHome?.addEventListener('click', () => fitMapHome(mapFeatures(), true));
    mapRetry?.addEventListener('click', initializeMap);

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!state.country || !state.city) {
        openLocationEditor();
        setMessage('Confirma tu ubicación antes de registrarte.', true);
        return;
      }

      if (!state.latitude || !state.longitude) {
        try {
          const geocoded = await geocodeLocation(state.city, state.country);
          Object.assign(state, geocoded, { source: state.source || 'geocoding', savedAt: Date.now() });
          updateLocationUI();
          saveLocation();
        } catch (error) {
          setMessage('No pudimos ubicar tu ciudad en el mapa. Revisa los datos.', true);
          openLocationEditor();
          return;
        }
      }

      const originalLabel = submitLabel?.textContent || '';
      if (submit) submit.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Procesando…';
      setMessage('');

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'No pudimos completar el registro.');

        window.location.href = section.dataset.directCheckout === 'true'
          ? '/checkout'
          : `${window.Shopify.routes.root}cart`;
      } catch (error) {
        if (submit) submit.disabled = false;
        if (submitLabel) submitLabel.textContent = originalLabel;
        setMessage(error.message || 'Ocurrió un problema. Inténtalo nuevamente.', true);
      }
    });

    section.querySelectorAll('[data-country-code]').forEach((element) => {
      const rawCode =
        element.dataset.countryCode ||
        element.textContent;

      const code = String(rawCode || '')
        .trim()
        .toUpperCase();

      element.textContent = countryFlag(code);
      element.setAttribute(
        'aria-label',
        code ? `Bandera ${code}` : 'País'
      );
    });

    const relatedCards = Array.from(
      section.querySelectorAll('[data-artmeter-related-card]')
    );

    relatedCards
      .slice()
      .sort((a, b) => (
        Number(b.dataset.registrations) -
          Number(a.dataset.registrations) ||
        Number(b.dataset.created) -
          Number(a.dataset.created)
      ))
      .forEach((card, index) => {
        const badge = card.querySelector(
          '[data-artmeter-related-badge]'
        );

        if (!badge) return;

        const created = Number(card.dataset.created) * 1000;
        const isNew =
          created > 0 &&
          Date.now() - created <= 30 * 86400000;

        let label = '';

        if (
          index === 0 &&
          Number(card.dataset.registrations) > 0
        ) {
          label = 'Más apoyado';
        } else if (isNew) {
          label = 'Nuevo';
        } else if (index < Math.min(5, relatedCards.length)) {
          label = 'Tendencia';
        }

        badge.textContent = label;
        badge.hidden = !label;
      });

    initializeReveal();
    startTicker();
    initializeMap();

    if (!loadSavedLocation()) detectLocationByIP();
    improveWithGrantedGeolocation();

    window.addEventListener('resize', () => map?.resize(), { passive: true });
    window.addEventListener('pagehide', () => {
      if (tickerTimer) window.clearInterval(tickerTimer);
    }, { once: true });
  });
})();
